import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import ProStatusToggle from "./ProStatusToggle";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { supabase } from "../../../supabase/supabase";
import { toast } from "@/components/ui/use-toast";
import OrganizationMembers from "./OrganizationMembers";
import OrganizationClientMembers from "./OrganizationClientMembers";
import WebhooksTab from "./WebhooksTab";
import ApiKeysTab from "./ApiKeysTab";
import ProvidersTab from "./ProvidersTab";
import WhatsAppNumbersTab from "./WhatsAppNumbersTab";

export default function OrganizationSettings() {
  const { organizationId } = useParams<{ organizationId: string }>();
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (organizationId) {
      fetchOrganization();
    }
  }, [organizationId]);

  const fetchOrganization = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from("organizations")
        .select("*")
        .eq("id", organizationId)
        .single();

      if (error) throw error;
      setOrganization(data);
    } catch (error: any) {
      console.error("Error fetching organization:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to fetch organization",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!organization) {
    return (
      <div className="flex justify-center items-center h-full">
        <div className="text-center">
          <h2 className="text-lg font-medium">Organization not found</h2>
          <p className="text-sm text-muted-foreground">
            The organization you are looking for does not exist or you do not
            have access to it.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">
          {organization.name} Settings
        </h1>
        <p className="text-muted-foreground">
          Manage your organization settings and members.
        </p>
      </div>

      <Tabs defaultValue="general" className="w-full">
        <TabsList className="grid w-full grid-cols-7">
          <TabsTrigger value="general">General</TabsTrigger>
          <TabsTrigger value="members">Members</TabsTrigger>
          <TabsTrigger value="client-members">Client Members</TabsTrigger>
          <TabsTrigger value="whatsapp">WhatsApp</TabsTrigger>
          <TabsTrigger value="webhooks">Webhooks</TabsTrigger>
          <TabsTrigger value="api-keys">API Keys</TabsTrigger>
          {organization.is_pro && (
            <TabsTrigger value="billing">Billing & Plan</TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="general" className="space-y-4 mt-4">
          <Card>
            <CardHeader>
              <CardTitle>General Settings</CardTitle>
              <CardDescription>
                Manage your organization's general settings.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-medium">Organization Name</h3>
                  <p className="text-sm text-muted-foreground">
                    {organization.name}
                  </p>
                </div>
                <div>
                  <h3 className="text-lg font-medium">Organization ID</h3>
                  <p className="text-sm font-mono text-muted-foreground">
                    {organization.id}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="members" className="space-y-4 mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Organization Members</CardTitle>
              <CardDescription>
                Manage your organization's members and their roles.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <OrganizationMembers organizationId={organizationId || ""} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="client-members" className="space-y-4 mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Client Members</CardTitle>
              <CardDescription>
                Manage client members who can access specific campaigns and
                leads.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <OrganizationClientMembers
                organizationId={organizationId || ""}
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="whatsapp" className="space-y-4 mt-4">
          <Card>
            <CardHeader>
              <CardTitle>WhatsApp Numbers</CardTitle>
              <CardDescription>
                Manage WhatsApp Business numbers for your organization.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <WhatsAppNumbersTab organizationId={organizationId || ""} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="webhooks" className="space-y-4 mt-4">
          <WebhooksTab />
        </TabsContent>

        <TabsContent value="api-keys" className="space-y-4 mt-4">
          <ApiKeysTab />
        </TabsContent>

        {organization.is_pro && (
          <TabsContent value="billing" className="space-y-4 mt-4">
            <ProStatusToggle
              organizationId={organizationId || ""}
              initialProStatus={organization?.is_pro || false}
              onStatusChange={(newStatus) => {
                setOrganization((prev) =>
                  prev ? { ...prev, is_pro: newStatus } : null,
                );
              }}
            />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
